import { type ReactNode } from "react"
import { motion } from "motion/react"
import { Check, Phone01 } from "@untitledui/icons"
import { cx } from "@/lib/utils/cx"
import {
  formatMoney,
  type ActivationView,
  type OnboardingStatus,
} from "@/lib/api/onboarding-activation"
import { BRAND_PINE, BRAND_EMERALD, BRAND_CHARCOAL } from "./ui"

// ============================================================================
// Standalone activation surface — classic setup-wizard structure with the
// brand's own contrast: a deep-pine sidebar (logo in white, emerald glows,
// vertical steps, "Need help?" card) against a clean white work area. The
// content pane carries a slowly shimmering brand-gradient hairline. Inter
// everywhere. No payment is collected on this surface.
// ============================================================================

export type StepKey = "shop" | "systems" | "contact" | "sign" | "book"

const SIDEBAR_INK = "#0B2E22" // deep pine — sidebar ground

const STEPS: { key: StepKey; label: string }[] = [
  { key: "shop", label: "Shop details" },
  { key: "systems", label: "Systems you use" },
  { key: "contact", label: "IT & billing" },
  { key: "sign", label: "Data authorization" },
  { key: "book", label: "Installation call" },
]

// The three intake steps share one server status (details_completed), so the
// stepper also treats every step BEFORE the current one as done.
const STATUS_RANK: Record<OnboardingStatus, number> = {
  link_generated: 0,
  email_sent: 0,
  opened: 0,
  details_completed: 1,
  agreement_signed: 2,
  call_booked: 3,
  expired: 0,
}

function statusDone(status: OnboardingStatus, key: StepKey): boolean {
  const r = STATUS_RANK[status] ?? 0
  if (key === "sign") return r >= 2
  if (key === "book") return r >= 3
  return r >= 1 // shop / systems / contact
}

/* ── The brand mark ─────────────────────────────────────── */
// Faithful recreation of the official eTX ImpaXt logo: emerald corner
// brackets (a viewfinder), "eTX" over "ImpaXt", spaced tagline. The full
// lockup animates once on load — brackets draw in from outside, the type
// rises in sequence. `dark` renders the white-on-pine variant.

const easeOut = [0.16, 1, 0.3, 1] as const
const riseIn = {
  hidden: { opacity: 0, y: 8 },
  show: { opacity: 1, y: 0, transition: { duration: 0.45, ease: easeOut } },
}

export function BrandLogo({ dark }: { dark?: boolean }) {
  const brackets = [
    { cls: "left-0 top-0 border-l-2 border-t-2 rounded-tl-[2px]", x: -10, y: -10 },
    { cls: "right-0 top-0 border-r-2 border-t-2 rounded-tr-[2px]", x: 10, y: -10 },
    { cls: "bottom-0 left-0 border-b-2 border-l-2 rounded-bl-[2px]", x: -10, y: 10 },
    { cls: "bottom-0 right-0 border-b-2 border-r-2 rounded-br-[2px]", x: 10, y: 10 },
  ]
  return (
    <motion.div
      initial="hidden"
      animate="show"
      variants={{ hidden: {}, show: { transition: { staggerChildren: 0.08, delayChildren: 0.1 } } }}
      className="relative inline-flex flex-col items-center px-6 py-4"
      aria-label="eTX ImpaXt — Collision · AI · Intelligence"
    >
      {brackets.map((b) => (
        <motion.span
          key={b.cls}
          aria-hidden
          variants={{
            hidden: { opacity: 0, x: b.x, y: b.y },
            show: { opacity: 1, x: 0, y: 0, transition: { duration: 0.5, ease: easeOut } },
          }}
          className={cx("absolute size-3", b.cls)}
          style={{ borderColor: dark ? "#34D399" : BRAND_EMERALD }}
        />
      ))}
      <motion.span
        variants={riseIn}
        className="text-[32px] font-black leading-none tracking-tight"
        style={{ color: dark ? "#FFFFFF" : BRAND_PINE }}
      >
        eTX
      </motion.span>
      <motion.span
        variants={riseIn}
        className="mt-1 text-[15.5px] font-extrabold leading-none tracking-tight"
        style={{ color: dark ? "rgba(255,255,255,0.85)" : BRAND_CHARCOAL }}
      >
        ImpaXt
      </motion.span>
      <motion.span
        variants={riseIn}
        className="mt-2 whitespace-nowrap text-[7.5px] font-semibold uppercase leading-none tracking-[0.26em]"
        style={{ color: dark ? "#6EE7B7" : BRAND_EMERALD }}
      >
        Collision · AI · Intelligence
      </motion.span>
    </motion.div>
  )
}

// Compact one-line mark for tight spots (mobile bar).
export function Wordmark({ dark }: { dark?: boolean }) {
  return (
    <div className="flex items-baseline gap-1.5">
      <span
        className="text-[17px] font-black leading-none tracking-tight"
        style={{ color: dark ? "#FFFFFF" : BRAND_PINE }}
      >
        eTX
      </span>
      <span
        className="text-[13.5px] font-extrabold leading-none tracking-tight"
        style={{ color: dark ? "rgba(255,255,255,0.8)" : BRAND_CHARCOAL }}
      >
        ImpaXt
      </span>
    </div>
  )
}

/* ── Layout ─────────────────────────────────────────────── */

export function ActivationLayout({
  data,
  current,
  welcome,
  onStepClick,
  children,
}: {
  data: ActivationView
  current: StepKey
  // On the cover screen the step list shows the whole job queued — nothing
  // active yet.
  welcome?: boolean
  // When provided for a step, that sidebar entry becomes clickable (used to
  // jump back to an earlier step). Forward jumps are never offered.
  onStepClick?: (key: StepKey) => void
  children: ReactNode
}) {
  return (
    <div className="min-h-screen bg-white">
      {/* fixed sidebar — desktop, deep pine with quiet emerald atmosphere */}
      <aside
        className="fixed inset-y-0 left-0 hidden w-[280px] flex-col overflow-hidden px-7 py-7 lg:flex"
        style={{ backgroundColor: SIDEBAR_INK }}
      >
        <div aria-hidden className="pointer-events-none absolute inset-0">
          <div className="absolute -right-24 -top-24 size-[300px] rounded-full bg-emerald-500/15 blur-3xl" />
          <div className="absolute -left-20 bottom-16 size-[260px] rounded-full bg-emerald-400/10 blur-3xl" />
        </div>

        <div className="relative flex min-h-0 flex-1 flex-col">
          <div className="flex justify-center">
            <BrandLogo dark />
          </div>

          {/* whose onboarding this is — the business, not a person */}
          <div className="mt-7 border-b border-white/10 pb-5">
            <p className="text-[11.5px] font-medium text-emerald-300/80">Onboarding</p>
            <p className="mt-0.5 text-[14px] font-semibold leading-snug text-white">
              {data.shop.name}
            </p>
          </div>

          <nav className="mt-7 flex-1" aria-label="Onboarding progress">
            <VerticalSteps data={data} current={current} welcome={welcome} onStepClick={onStepClick} />
          </nav>

          {/* help — the manager, clearly separated at the bottom */}
          <div className="mt-8 rounded-xl bg-white/[0.06] p-4 ring-1 ring-white/10">
            <p className="text-[12px] font-semibold text-white/60">Need help?</p>
            <div className="mt-2.5 flex items-center gap-3">
              <span className="grid size-9 shrink-0 place-items-center rounded-full bg-emerald-400/20 text-[12px] font-bold text-emerald-200 ring-1 ring-emerald-400/30">
                {data.salesRep.name.split(" ").map((n) => n[0]).join("").slice(0, 2)}
              </span>
              <div className="min-w-0">
                <p className="truncate text-[13px] font-semibold text-white">{data.salesRep.name}</p>
                <p className="text-[11.5px] text-white/50">Your onboarding manager</p>
              </div>
            </div>
            {data.salesRep.phone && (
              <a
                href={`tel:${data.salesRep.phone.replace(/\D/g, "")}`}
                className="mt-3 flex items-center justify-center gap-1.5 rounded-lg bg-white py-2 text-[13px] font-semibold transition-colors hover:bg-emerald-50"
                style={{ color: SIDEBAR_INK }}
              >
                <Phone01 className="size-3.5 text-emerald-600" aria-hidden />
                {data.salesRep.phone}
              </a>
            )}
          </div>
        </div>
      </aside>

      {/* mobile header */}
      <MobileHeader data={data} current={current} welcome={welcome} />

      {/* content — clean white, with a slowly shimmering brand hairline */}
      <div className="lg:pl-[280px]">
        <ShimmerLine className="hidden lg:block" />
        <main className="mx-auto w-full max-w-[640px] px-5 pb-14 pt-6 md:px-8 lg:pt-12">
          {children}
          {/* welcome shows the full SubscriptionDetails panel instead */}
          {!welcome && <DealStrip data={data} />}
        </main>
      </div>
    </div>
  )
}

// The brand-gradient hairline, alive: the gradient pans slowly, a quiet
// shine rather than an effect.
function ShimmerLine({ className }: { className?: string }) {
  return (
    <motion.div
      aria-hidden
      className={cx("h-[3px]", className)}
      style={{
        background: `linear-gradient(90deg, ${BRAND_PINE}, ${BRAND_EMERALD}, #34D399, ${BRAND_EMERALD}, ${BRAND_PINE})`,
        backgroundSize: "200% 100%",
      }}
      animate={{ backgroundPosition: ["0% 0%", "200% 0%"] }}
      transition={{ duration: 9, repeat: Infinity, ease: "linear" }}
    />
  )
}

/* ── Vertical steps (sidebar, on pine) ──────────────────── */

function VerticalSteps({
  data,
  current,
  welcome,
  onStepClick,
}: {
  data: ActivationView
  current: StepKey
  welcome?: boolean
  onStepClick?: (key: StepKey) => void
}) {
  const currentIdx = welcome ? -1 : STEPS.findIndex((s) => s.key === current)

  return (
    <ol>
      {STEPS.map((s, i) => {
        const done = statusDone(data.status, s.key) || (currentIdx >= 0 && i < currentIdx)
        const active = !done && i === currentIdx
        const last = i === STEPS.length - 1
        // Only already-visited steps are clickable — jumping back, never ahead.
        const clickable = Boolean(onStepClick) && currentIdx >= 0 && i < currentIdx
        const inner = (
          <>
            <motion.span
              key={done ? "done" : active ? "active" : "todo"}
              initial={{ scale: 0.8 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", stiffness: 500, damping: 22 }}
              className={cx(
                "grid size-[27px] shrink-0 place-items-center rounded-full border text-[12px] font-semibold transition-colors duration-300",
                done
                  ? "border-emerald-400 bg-emerald-400 text-[#0B2E22]"
                  : active
                    ? "border-emerald-300 bg-white/10 text-emerald-200 ring-4 ring-emerald-400/20"
                    : "border-white/20 bg-transparent text-white/40",
              )}
            >
              {done ? <Check className="size-3.5" strokeWidth={3.5} /> : i + 1}
            </motion.span>
            <span
              className={cx(
                "text-[13.5px] leading-tight transition-colors duration-300",
                done
                  ? "font-medium text-white/75"
                  : active
                    ? "font-semibold text-white"
                    : "font-medium text-white/40",
                clickable && "group-hover:text-white group-hover:underline group-hover:decoration-white/40 group-hover:underline-offset-2",
              )}
            >
              {s.label}
            </span>
          </>
        )
        return (
          <li key={s.key} className="relative pb-7 last:pb-0">
            {!last && (
              <span
                aria-hidden
                className={cx(
                  "absolute left-[13px] top-[30px] h-[calc(100%-34px)] w-px",
                  done ? "bg-emerald-400/70" : "bg-white/15",
                )}
              />
            )}
            {clickable ? (
              <button
                type="button"
                onClick={() => onStepClick!(s.key)}
                className="group flex w-full items-center gap-3 rounded-md text-left"
              >
                {inner}
              </button>
            ) : (
              <div className="flex items-center gap-3">{inner}</div>
            )}
          </li>
        )
      })}
    </ol>
  )
}

/* ── Mobile header (pine) ───────────────────────────────── */

function MobileHeader({
  data,
  current,
  welcome,
}: {
  data: ActivationView
  current: StepKey
  welcome?: boolean
}) {
  const currentIdx = welcome ? -1 : STEPS.findIndex((s) => s.key === current)
  const doneCount = STEPS.filter(
    (s, i) => statusDone(data.status, s.key) || (currentIdx >= 0 && i < currentIdx),
  ).length
  const label = currentIdx >= 0 ? STEPS[currentIdx]?.label : "Welcome"
  const pct = welcome ? 0 : Math.round((doneCount / STEPS.length) * 100)

  return (
    <div className="sticky top-0 z-30 lg:hidden" style={{ backgroundColor: SIDEBAR_INK }}>
      <div className="flex items-center justify-between gap-3 px-5 pt-3">
        <Wordmark dark />
        {data.salesRep.phone && (
          <a
            href={`tel:${data.salesRep.phone.replace(/\D/g, "")}`}
            className="flex items-center gap-1.5 rounded-lg bg-white/10 px-2.5 py-1.5 text-[12px] font-semibold text-white ring-1 ring-white/15"
          >
            <Phone01 className="size-3.5 text-emerald-300" aria-hidden />
            Call {data.salesRep.name.split(" ")[0]}
          </a>
        )}
      </div>
      <div className="px-5 pb-3 pt-2.5">
        <div className="flex items-baseline justify-between">
          <p className="text-[12.5px] font-semibold text-white">
            {welcome ? "Welcome" : `Step ${currentIdx + 1} of ${STEPS.length}`}
            {!welcome && <span className="font-medium text-white/55"> — {label}</span>}
          </p>
          {!welcome && <span className="text-[11.5px] text-white/50">{doneCount}/{STEPS.length} done</span>}
        </div>
        <div className="mt-1.5 h-1 overflow-hidden rounded-full bg-white/15">
          <span
            className="block h-full rounded-full bg-emerald-400 transition-all duration-500"
            style={{ width: `${welcome ? 4 : Math.max(pct, 8)}%` }}
          />
        </div>
      </div>
      <ShimmerLine />
    </div>
  )
}

/* ── Subscription details — always visible, never behind a click ── */

// The full panel — shown on the welcome screen.
export function SubscriptionDetails({ data }: { data: ActivationView }) {
  const p = data.pricing
  return (
    <div className="rounded-xl border border-[#E8ECEA] bg-[#FAFBFA] px-5 py-4">
      <div className="space-y-2">
        {p.lineItems.map((li) => (
          <div key={li.description} className="flex items-baseline gap-2.5">
            <span className="shrink-0 text-[13.5px] text-[#4A5A51]">
              {li.description}
              {li.kind === "dealership" && (
                <span className="ml-1.5 text-[11px] font-medium text-[#96A29A]">· dealership</span>
              )}
            </span>
            <span aria-hidden className="mb-1 min-w-4 flex-1 border-b border-dotted border-[#D5DDD8]" />
            <span className="shrink-0 text-[13.5px] font-semibold tabular-nums text-[#33413A]">
              {formatMoney(li.monthlyAmountCents)}
            </span>
          </div>
        ))}
      </div>
      <div className="mt-3 flex items-baseline justify-between border-t border-[#E8ECEA] pt-2.5">
        <span className="text-[13.5px] font-semibold text-[#33413A]">Monthly total</span>
        <span className="text-[15px] font-bold tabular-nums text-[#0C2018]">
          {formatMoney(p.monthlyTotalCents)}
          <span className="text-[11.5px] font-normal text-[#8A9890]">/mo</span>
        </span>
      </div>
      <p className="mt-2 text-[12.5px] leading-relaxed text-[#8A9890]">
        {p.contractTermMonths}-month term, invoiced separately. Nothing is charged today —{" "}
        {data.salesRep.name} will confirm billing on your installation call.
      </p>
    </div>
  )
}

// The one-line reminder under every other step — plain text, nothing to click.
function DealStrip({ data }: { data: ActivationView }) {
  const p = data.pricing
  return (
    <p className="mt-8 border-t border-[#EDF1EE] pt-3 text-[12.5px] text-[#96A29A]">
      Nothing is charged today · Subscription {formatMoney(p.monthlyTotalCents)}/mo ·{" "}
      {p.contractTermMonths}-month term
    </p>
  )
}

/* ── Bare shell (loading / error / dead-link screens) ───── */

export function BareShell({ children }: { children: ReactNode }) {
  return (
    <div className="grid min-h-screen place-items-center bg-white px-6">
      <div className="w-full max-w-md">
        <div className="mb-8 flex justify-center">
          <BrandLogo />
        </div>
        {children}
      </div>
    </div>
  )
}
